// Types used by the OfflineStorageService

interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  name: string;
  radius: number;
  cachedAt: number;
  tiles: string[];
}

// Minimal Overpass API response types
interface OverpassCenter { lat: number; lon: number }
interface OverpassElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  center?: OverpassCenter;
  tags?: Record<string, string>;
}
interface OverpassResponse { elements: OverpassElement[] }

interface NominatimAddress {
  road?: string;
  house_number?: string;
  suburb?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  [key: string]: string | number | undefined;
}

interface AddressEntry {
  id: string;
  lat: number;
  lng: number;
  address: NominatimAddress;
  cachedAt: number;
  searchTerms: string[];
}

interface OfflineVisit {
  id: string;
  timestamp: number;
  synced: boolean;
  // Allow additional metadata fields for offline capture
  [key: string]: string | number | boolean | undefined;
}

interface StorageStatus {
  hasMapCache: boolean;
  cachedPointsCount: number;
  totalVisits: number;
  pendingSync: number;
  lastCacheUpdate: number | null;
}

interface MapSettings {
  cacheRadius: number;
  cacheExpiration: number;
  autoCache: boolean;
  maxCachePoints: number;
  addressCacheRadius: number;
}

class OfflineStorageService {
  // Storage keys
  private readonly KEYS = {
    MAP_CACHE: 'ide_map_cache',
    CACHED_POINTS: 'ide_cached_points',
    OFFLINE_VISITS: 'ide_offline_visits',
    MAP_SETTINGS: 'ide_map_settings',
    SYNC_QUEUE: 'ide_sync_queue',
    ADDRESS_CACHE: 'ide_address_cache',
  } as const;

  // Default settings
  private readonly DEFAULT_SETTINGS: MapSettings = {
    cacheRadius: 1.5, // km
    cacheExpiration: 6, // months
    autoCache: true,
    maxCachePoints: 50,
    addressCacheRadius: 0.1, // radius in km for address cache search
  };

  /**
   * Check if map cache exists
   */
  hasMapCache(): boolean {
    return localStorage.getItem(this.KEYS.MAP_CACHE) !== null;
  }

  /**
   * Get map settings
   */
  getMapSettings(): MapSettings {
    const stored = localStorage.getItem(this.KEYS.MAP_SETTINGS);
    return stored ? JSON.parse(stored) : this.DEFAULT_SETTINGS;
  }

  /**
   * Save map settings
   */
  saveMapSettings(settings: Partial<MapSettings>): void {
    const current = this.getMapSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(this.KEYS.MAP_SETTINGS, JSON.stringify(updated));
  }

  /**
   * Get cached points (filtered by expiration)
   */
  getCachedPoints(): MapPoint[] {
    const stored = localStorage.getItem(this.KEYS.CACHED_POINTS);
    if (!stored) return [];

    const points: MapPoint[] = JSON.parse(stored);
    const now = Date.now();
    const settings = this.getMapSettings();
    const expirationTime = settings.cacheExpiration * 30 * 24 * 60 * 60 * 1000; // months to ms

    // Filter expired points
    return points.filter(point => (now - point.cachedAt) < expirationTime);
  }

  /**
   * Save a point to cache
   */
  saveCachedPoint(
    lat: number,
    lng: number,
    name: string = '',
    radius: number | null = null
  ): MapPoint {
    const points = this.getCachedPoints();
    const settings = this.getMapSettings();
    const cacheRadius = radius || settings.cacheRadius;

    const newPoint: MapPoint = {
      id: Date.now().toString(),
      lat,
      lng,
      name: name || `Ponto ${points.length + 1}`,
      radius: cacheRadius,
      cachedAt: Date.now(),
      tiles: [],
    };

    points.push(newPoint);

    // Limit maximum number of points
    if (points.length > settings.maxCachePoints) {
      points.shift(); // Remove oldest
    }

    localStorage.setItem(this.KEYS.CACHED_POINTS, JSON.stringify(points));
    return newPoint;
  }

  /**
   * Remove point from cache
   */
  removeCachedPoint(pointId: string): void {
    const points = this.getCachedPoints();
    const filtered = points.filter(p => p.id !== pointId);
    localStorage.setItem(this.KEYS.CACHED_POINTS, JSON.stringify(filtered));
  }
  
  /**
   * Update cached point timestamp (renew expiration)
   * @param pointId ID of the point to update
   * @returns Updated point or null if not found
   */
  updateCachedPointTimestamp(pointId: string): MapPoint | null {
    const points = this.getCachedPoints();
    const pointIndex = points.findIndex(p => p.id === pointId);
    
    if (pointIndex === -1) return null;
    
    // Update the timestamp
    const updatedPoint = {
      ...points[pointIndex],
      cachedAt: Date.now()
    };
    
    // Save the updated point
    points[pointIndex] = updatedPoint;
    localStorage.setItem(this.KEYS.CACHED_POINTS, JSON.stringify(points));
    
    return updatedPoint;
  }

  /**
   * Cache address data
   */
  cacheAddress(lat: number, lng: number, addressData: NominatimAddress): AddressEntry {
    const cached = this.getCachedAddresses();

    const addressEntry: AddressEntry = {
      id: `addr_${Date.now()}`,
      lat: parseFloat(lat.toFixed(6)),
      lng: parseFloat(lng.toFixed(6)),
      address: addressData,
      cachedAt: Date.now(),
      // Extra info for offline search
      searchTerms: [
        addressData.road || '',
        addressData.house_number || '',
        addressData.suburb || '',
        addressData.city || '',
        addressData.state || '',
      ]
        .filter(Boolean)
        .map(term => term.toLowerCase()),
    };

    // Check if there's already a very close address (avoid duplicates)
    const existing = cached.find(addr => {
      const distance = this.calculateDistance(lat, lng, addr.lat, addr.lng);
      return distance < 0.01; // 10 meters
    });

    if (existing) {
      // Update existing entry
      const index = cached.findIndex(addr => addr.id === existing.id);
      cached[index] = { ...existing, ...addressEntry, id: existing.id };
    } else {
      cached.push(addressEntry);
    }

    // Limit cache size (keep only most recent)
    if (cached.length > 1000) {
      cached.sort((a, b) => b.cachedAt - a.cachedAt);
      cached.splice(800); // Keep only 800 most recent
    }

    localStorage.setItem(this.KEYS.ADDRESS_CACHE, JSON.stringify(cached));
    return addressEntry;
  }

  /**
   * Get cached addresses (filtered by expiration)
   */
  getCachedAddresses(): AddressEntry[] {
    const stored = localStorage.getItem(this.KEYS.ADDRESS_CACHE);
    if (!stored) return [];

    const addresses: AddressEntry[] = JSON.parse(stored);
    const now = Date.now();
    const settings = this.getMapSettings();
    const expirationTime = settings.cacheExpiration * 30 * 24 * 60 * 60 * 1000;

    // Filter expired addresses
    return addresses.filter(addr => (now - addr.cachedAt) < expirationTime);
  }

  /**
   * Find cached address by coordinates
   */
  findCachedAddress(lat: number, lng: number): AddressEntry | null {
    const cached = this.getCachedAddresses();
    const settings = this.getMapSettings();
    const searchRadius = settings.addressCacheRadius || 0.1; // 100 meters

    // Find closest address within radius
    let closestAddress: AddressEntry | null = null;
    let closestDistance = Infinity;

    for (const addr of cached) {
      const distance = this.calculateDistance(lat, lng, addr.lat, addr.lng);
      if (distance <= searchRadius && distance < closestDistance) {
        closestAddress = addr;
        closestDistance = distance;
      }
    }

    return closestAddress;
  }

  /**
   * Calculate distance between two points (simplified haversine formula)
   */
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) *
        Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Search cached addresses by search term (offline)
   */
  searchCachedAddresses(searchTerm: string): AddressEntry[] {
    if (!searchTerm || searchTerm.length < 2) return [];

    const cached = this.getCachedAddresses();
    const searchLower = searchTerm.toLowerCase();

    return cached
      .filter(addr => addr.searchTerms.some(term => term.includes(searchLower)))
      .slice(0, 10); // Limit to 10 results
  }

  /**
   * Preload addresses for an area (to use when online)
   */
  async preloadAreaAddresses(
    centerLat: number,
    centerLng: number,
    radiusKm: number = 1
  ): Promise<number> {
    const points: Array<{ lat: number; lng: number }> = [];
    // Adaptive step in kilometers: finer for small radii, capped for larger
    const stepKm = Math.max(0.1, Math.min(0.5, radiusKm / 3));
    const steps = Math.max(1, Math.ceil(radiusKm / stepKm));

    const kmToDegLat = (km: number) => km / 111; // approx
    const kmToDegLng = (km: number) => km / (111 * Math.cos(centerLat * Math.PI / 180) || 1);

    // Create a grid of points to search for addresses (km-based, then converted to degrees)
    for (let i = -steps; i <= steps; i++) {
      for (let j = -steps; j <= steps; j++) {
        const lat = centerLat + kmToDegLat(i * stepKm);
        const lng = centerLng + kmToDegLng(j * stepKm);
        const distance = this.calculateDistance(centerLat, centerLng, lat, lng);

        if (distance <= radiusKm) {
          points.push({ lat, lng });
        }
      }
    }

    // Process points in smaller batches to avoid overloading the API
    const batchSize = 3;
    let cached = 0;

    for (let i = 0; i < points.length; i += batchSize) {
      const batch = points.slice(i, i + batchSize);

      await Promise.all(
        batch.map(async point => {
          try {
            // Skip if a nearby address is already cached
            const existing = this.findCachedAddress(point.lat, point.lng);
            if (existing) return;

            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${point.lat}&lon=${point.lng}&addressdetails=1&accept-language=pt-BR,pt,en`
            );

            if (response.ok) {
              const data = await response.json();
              if (data && data.address) {
                this.cacheAddress(point.lat, point.lng, data.address);
                cached++;
              }
            }

            // Polite delay to respect API limits
            await new Promise(resolve => setTimeout(resolve, 120));
          } catch (error) {
            console.warn('Error preloading address:', error);
          }
        })
      );
    }

    return cached;
  }

  /**
   * Preload unique streets for an area using Overpass API.
   * Queries OSM ways/relations with highway+name inside the radius and caches as address entries with only the street (no house numbers).
   * Radius is in kilometers.
   */
  async preloadAreaAddressesOverpass(
    centerLat: number,
    centerLng: number,
    radiusKm: number = 1
  ): Promise<number> {
    try {
      const radiusMeters = Math.max(50, Math.floor(radiusKm * 1000));
      // Query streets (ways/relations) with highway + name within radius; include center for geometry-less elements
      const overpassQuery = `[out:json][timeout:25];(way(around:${radiusMeters},${centerLat},${centerLng})["highway"]["name"];relation(around:${radiusMeters},${centerLat},${centerLng})["highway"]["name"];);out center;`;

      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${encodeURIComponent(overpassQuery)}`,
      });

      if (!response.ok) {
        console.warn('Overpass request failed with status:', response.status);
        return 0;
      }

      const data = (await response.json()) as OverpassResponse;
      const elements: OverpassElement[] = Array.isArray(data?.elements) ? data.elements : [];
      let cached = 0;

      // Build a set of existing street names in the target area to avoid duplicates
      const existing = this.getCachedAddresses();
      const normalize = (name: string, city?: string) => `${name}`.trim().toLowerCase() + (city ? `|${city.trim().toLowerCase()}` : '');
      const inArea = (addrLat: number, addrLng: number) => this.calculateDistance(centerLat, centerLng, addrLat, addrLng) <= radiusKm;
      const existingStreets = new Set(
        existing
          .filter(a => a.address.road && inArea(a.lat, a.lng))
          .map(a => normalize(a.address.road as string, (a.address.city as string | undefined)))
      );
      const sessionStreets = new Set<string>();

      for (const el of elements) {
        const tags = el?.tags || {};
        const name = tags['name'];
        if (!name) continue;

        // Determine representative coordinates for the street
        let lat: number | null = null;
        let lng: number | null = null;
        if (el.center && typeof el.center.lat === 'number' && typeof el.center.lon === 'number') {
          lat = el.center.lat;
          lng = el.center.lon;
        }
        if (lat == null || lng == null) continue;

        const city = tags['addr:city'] || tags['is_in:city'] || undefined;
        const key = normalize(name, city);
        if (existingStreets.has(key) || sessionStreets.has(key)) continue;

        // Build a minimal Nominatim-like address object with just the street
        const address: NominatimAddress = {
          road: name,
          city: city,
        };

        this.cacheAddress(lat, lng, address);
        sessionStreets.add(key);
        cached++;
      }

      return cached;
    } catch (error) {
      console.warn('Error preloading addresses via Overpass:', error);
      return 0;
    }
  }

  /**
   * Save offline visit
   */
  saveOfflineVisit(visitData: Record<string, unknown>): OfflineVisit {
    const visits = this.getOfflineVisits();
    const newVisit: OfflineVisit = {
      id: `offline_${Date.now()}`,
      // Spread only primitive-safe values
      ...(visitData as Record<string, string | number | boolean | undefined>),
      timestamp: Date.now(),
      synced: false,
    };

    visits.push(newVisit);
    localStorage.setItem(this.KEYS.OFFLINE_VISITS, JSON.stringify(visits));
    return newVisit;
  }

  /**
   * Get offline visits
   */
  getOfflineVisits(): OfflineVisit[] {
    const stored = localStorage.getItem(this.KEYS.OFFLINE_VISITS);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Mark visit as synced
   */
  markVisitAsSynced(visitId: string): void {
    const visits = this.getOfflineVisits();
    const updated = visits.map(visit =>
      visit.id === visitId ? { ...visit, synced: true } : visit
    );
    localStorage.setItem(this.KEYS.OFFLINE_VISITS, JSON.stringify(updated));
  }

  /**
   * Remove synced visits
   */
  removeSyncedVisits(): void {
    const visits = this.getOfflineVisits();
    const pending = visits.filter(visit => !visit.synced);
    localStorage.setItem(this.KEYS.OFFLINE_VISITS, JSON.stringify(pending));
  }

  /**
   * Get storage status
   */
  getStorageStatus(): StorageStatus {
    const cachedPoints = this.getCachedPoints();
    const offlineVisits = this.getOfflineVisits();
    const pendingVisits = offlineVisits.filter(v => !v.synced);

    return {
      hasMapCache: this.hasMapCache(),
      cachedPointsCount: cachedPoints.length,
      totalVisits: offlineVisits.length,
      pendingSync: pendingVisits.length,
      lastCacheUpdate:
        cachedPoints.length > 0
          ? Math.max(...cachedPoints.map(p => p.cachedAt))
          : null,
    };
  }

  /**
   * Clear all cache data
   */
  clearAllCache(): void {
    Object.values(this.KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Get cache size in bytes (approximate)
   */
  getCacheSize(): number {
    let totalSize = 0;
    Object.values(this.KEYS).forEach(key => {
      const item = localStorage.getItem(key);
      if (item) {
        totalSize += item.length * 2; // Approximate size in bytes (UTF-16)
      }
    });
    return totalSize;
  }
}

export const offlineStorageService = new OfflineStorageService();
export default offlineStorageService;
